/**
 * MarketingDriveWithUs.jsx · v2.7.65+E77 · Public "Drive With Us" page
 * ═══════════════════════════════════════════════════════════════════════
 * Public landing page that hosts the initial contractor application form.
 * On successful submit the applicant sees a friendly thank-you card;
 * their record lands in `applications` (Pending), viewable by staff at
 * Admin → User Management → Applications.
 *
 * The resume upload runs in TWO steps to sidestep proxy timeouts on
 * large PDFs:
 *   1. File selected → POST /api/applications/upload-resume (streamed to
 *      GridFS, ≤50MB, .pdf/.doc/.docx only). Returns { url, filename }.
 *   2. On submit → POST /api/applications with the resume_url embedded.
 */
import { useState } from "react";
import axios from "axios";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import SmsConsent from "@/components/marketing/SmsConsent";
import { toast } from "sonner";
import { Loader2, Upload, CheckCircle2, FileText, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_RESUME_MB = 50;
const ALLOWED_EXTS = [".pdf", ".doc", ".docx"];

const POSITION_GROUPS = [
  {
    label: "Operations & Dispatch",
    options: ["Taxi Driver", "Dispatch"],
  },
  {
    label: "Management & Safety",
    options: ["Team Leader", "Field Supervisor", "Safety Manager", "Compliance Manager"],
  },
  {
    label: "Sales & Accounts",
    options: ["Sales", "Account Manager"],
  },
  {
    label: "Customer Experience",
    options: ["Support"],
  },
];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

const initialForm = {
  first_name: "", middle_name: "", last_name: "",
  home_phone: "", cell_phone: "",
  email: "",
  address: "", city: "", state: "OK", zip: "",
  position_applied: "",
};

export default function MarketingDriveWithUs() {
  const [form, setForm] = useState(initialForm);
  const [resume, setResume] = useState(null);        // { url, filename }
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [fieldErr, setFieldErr] = useState({});
  // v2.7.65+E165 · TCPA-style SMS consent — recorded server-side with the
  // application. Optional (not required for form submit); disclosure block
  // rendered by <SmsConsent/> immediately above the submit button.
  const [smsConsent, setSmsConsent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = "Required";
    if (!form.last_name.trim())  errs.last_name  = "Required";
    if (!form.cell_phone.trim()) errs.cell_phone = "Required";
    else if (form.cell_phone.replace(/\D/g, "").length < 7) errs.cell_phone = "Invalid phone";
    if (!form.email.trim())      errs.email      = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Invalid email";
    if (!form.address.trim())    errs.address    = "Required";
    if (!form.city.trim())       errs.city       = "Required";
    if (!form.state.trim())      errs.state      = "Required";
    if (!form.zip.trim())        errs.zip        = "Required";
    else if (!/^\d{5}(-\d{4})?$/.test(form.zip.trim())) errs.zip = "Use 5-digit or 9-digit ZIP";
    if (!form.position_applied)  errs.position_applied = "Required";
    setFieldErr(errs);
    return Object.keys(errs).length === 0;
  };

  const onResumeChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!ALLOWED_EXTS.some((ext) => lower.endsWith(ext))) {
      toast.error(`Only ${ALLOWED_EXTS.join(", ")} resumes accepted`);
      e.target.value = "";
      return;
    }
    if (file.size > MAX_RESUME_MB * 1024 * 1024) {
      toast.error(`Resume must be ${MAX_RESUME_MB} MB or smaller`);
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/applications/upload-resume`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResume({ url: data.url, filename: data.filename, size: data.size });
      toast.success(`Resume uploaded · ${file.name}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Upload failed";
      toast.error(typeof msg === "string" ? msg : "Upload failed");
      setResume(null);
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fill in the required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        first_name: form.first_name.trim(),
        middle_name: form.middle_name.trim() || null,
        last_name: form.last_name.trim(),
        home_phone: form.home_phone.trim() || null,
        cell_phone: form.cell_phone.trim(),
        email: form.email.trim().toLowerCase(),
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        zip: form.zip.trim(),
        resume_url: resume?.url || null,
        resume_filename: resume?.filename || null,
        sms_consent: !!smsConsent,
        sms_consent_ts: smsConsent ? new Date().toISOString() : null,
      };
      await axios.post(`${API}/applications`, payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Submission failed";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarketingLayout>
      <section className="max-w-4xl mx-auto px-5 py-10 md:py-14" data-testid="drive-with-us-page">
        <div className="text-center mb-8">
          <div className="text-[10px] uppercase tracking-[0.35em] text-amber-400 font-black">
            Join The Team
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-zinc-100 mt-2">
            DRIVE FOR US
          </h1>
          <p className="text-zinc-400 mt-3 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            Buller Taxi is looking for professional operators, dispatchers, and support staff
            across Weatherford, OK and all 77 counties. Fill out the initial application below —
            a hiring manager will reach out to next steps.
          </p>
        </div>

        {done ? (
          <div
            className="rounded-lg border-2 border-emerald-500/60 bg-emerald-950/30 p-8 text-center"
            data-testid="drive-success"
          >
            <CheckCircle2 className="h-12 w-12 text-emerald-300 mx-auto mb-3" />
            <div className="text-lg font-black text-emerald-100">Application received</div>
            <p className="text-sm text-emerald-100/90 mt-2 leading-relaxed max-w-xl mx-auto">
              Thank you for submitting your application. Someone from our team will be in touch
              shortly. If you have question&apos;s you may call{" "}
              <a href="tel:5802194687" className="font-black text-amber-300 hover:text-amber-200">
                (580) 219-4687
              </a>.
            </p>
            <a
              href="/"
              className="inline-flex items-center gap-2 mt-6 bg-amber-400 text-zinc-950 hover:bg-amber-300 font-extrabold px-5 py-2 rounded"
              data-testid="drive-success-home"
            >
              Return to Home
            </a>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="space-y-6 bg-zinc-950 border border-zinc-800 rounded-lg p-6 md:p-8"
            data-testid="drive-with-us-form"
            noValidate
          >
            {/* Name row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="First Name" required error={fieldErr.first_name}>
                <input
                  data-testid="dw-first-name"
                  type="text"
                  value={form.first_name}
                  onChange={set("first_name")}
                  className={inputCls}
                  autoComplete="given-name"
                />
              </Field>
              <Field label="Middle Name" error={fieldErr.middle_name}>
                <input
                  data-testid="dw-middle-name"
                  type="text"
                  value={form.middle_name}
                  onChange={set("middle_name")}
                  className={inputCls}
                  autoComplete="additional-name"
                />
              </Field>
              <Field label="Last Name" required error={fieldErr.last_name}>
                <input
                  data-testid="dw-last-name"
                  type="text"
                  value={form.last_name}
                  onChange={set("last_name")}
                  className={inputCls}
                  autoComplete="family-name"
                />
              </Field>
            </div>

            {/* Phones + email */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Home Phone" error={fieldErr.home_phone}>
                <input
                  data-testid="dw-home-phone"
                  type="tel"
                  value={form.home_phone}
                  onChange={set("home_phone")}
                  className={inputCls}
                  placeholder="(555) 555-5555"
                  autoComplete="home tel"
                />
              </Field>
              <Field label="Cell Phone" required error={fieldErr.cell_phone}>
                <input
                  data-testid="dw-cell-phone"
                  type="tel"
                  value={form.cell_phone}
                  onChange={set("cell_phone")}
                  className={inputCls}
                  placeholder="(555) 555-5555"
                  autoComplete="mobile tel"
                />
              </Field>
              <Field label="Email Address" required error={fieldErr.email}>
                <input
                  data-testid="dw-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  className={inputCls}
                  autoComplete="email"
                />
              </Field>
            </div>

            {/* Address */}
            <Field label="Address" required error={fieldErr.address}>
              <input
                data-testid="dw-address"
                type="text"
                value={form.address}
                onChange={set("address")}
                className={inputCls}
                autoComplete="street-address"
              />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="City" required error={fieldErr.city}>
                <input
                  data-testid="dw-city"
                  type="text"
                  value={form.city}
                  onChange={set("city")}
                  className={inputCls}
                  autoComplete="address-level2"
                />
              </Field>
              <Field label="State" required error={fieldErr.state}>
                <select
                  data-testid="dw-state"
                  value={form.state}
                  onChange={set("state")}
                  className={inputCls}
                  autoComplete="address-level1"
                >
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="Zip Code" required error={fieldErr.zip}>
                <input
                  data-testid="dw-zip"
                  type="text"
                  value={form.zip}
                  onChange={set("zip")}
                  className={inputCls}
                  inputMode="numeric"
                  autoComplete="postal-code"
                  placeholder="73096"
                />
              </Field>
            </div>

            {/* Position */}
            <Field label="Position Applying For" required error={fieldErr.position_applied}>
              <select
                data-testid="dw-position"
                value={form.position_applied}
                onChange={set("position_applied")}
                className={inputCls}
              >
                <option value="">— Select a position —</option>
                {POSITION_GROUPS.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.options.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </Field>

            {/* Resume */}
            <div>
              <label className="block text-[11px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">
                Resume Upload <span className="text-zinc-600">· PDF / DOC / DOCX · ≤ {MAX_RESUME_MB} MB</span>
              </label>
              <div className="flex items-center gap-3 flex-wrap">
                <label
                  className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-amber-400/60 text-zinc-100 font-bold text-sm px-4 py-2.5 rounded cursor-pointer transition"
                  data-testid="dw-resume-label"
                >
                  {uploading
                    ? <Loader2 className="h-4 w-4 animate-spin"/>
                    : <Upload className="h-4 w-4"/>}
                  {uploading ? "Uploading…" : (resume ? "Replace resume" : "Choose resume file")}
                  <input
                    data-testid="dw-resume-input"
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={onResumeChange}
                    className="hidden"
                    disabled={uploading || submitting}
                  />
                </label>
                {resume && (
                  <span
                    className="inline-flex items-center gap-1.5 text-xs text-emerald-300 bg-emerald-950/40 border border-emerald-700/50 px-2.5 py-1 rounded"
                    data-testid="dw-resume-attached"
                  >
                    <FileText className="h-3.5 w-3.5"/>
                    {resume.filename}
                  </span>
                )}
              </div>
              <p className="text-[10px] text-zinc-500 mt-1.5">
                Uploading your resume is optional but strongly recommended.
              </p>
            </div>

            {/* Submit */}
            <div className="pt-2 border-t border-zinc-800 space-y-4">
              {/* v2.7.65+E165 · TCPA-style SMS consent — legal disclosure. */}
              <SmsConsent
                value={smsConsent}
                onChange={setSmsConsent}
                testIdPrefix="dw-sms-consent"
              />
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <button
                  type="submit"
                  disabled={submitting || uploading}
                  data-testid="dw-submit"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-50 text-zinc-950 font-black uppercase tracking-widest text-sm px-8 py-3 rounded transition"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin"/>}
                  {submitting ? "Submitting…" : "Submit Application"}
                </button>
                <p className="text-[11px] text-zinc-500 flex items-center gap-1.5">
                  <Phone className="h-3.5 w-3.5"/>
                  Questions? Call <a href="tel:5802194687" className="text-amber-300 hover:text-amber-200 font-bold">(580) 219-4687</a>
                </p>
              </div>
            </div>
          </form>
        )}
      </section>
    </MarketingLayout>
  );
}

const inputCls =
  "w-full bg-zinc-900 border border-zinc-700 focus:border-amber-400 rounded px-3 py-2.5 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none transition";

function Field({ label, required, error, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">
        {label} {required && <span className="text-rose-400">*</span>}
      </span>
      {children}
      {error && (
        <span className="block text-[11px] text-rose-400 mt-1" data-testid="dw-field-error">
          {error}
        </span>
      )}
    </label>
  );
}
